import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Send, CheckCircle, Loader2, User, Mail, Phone, MapPin, MessageSquare, Shield, Star, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hero = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    suburb: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/quote`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Suburb: ${formData.suburb}\n${formData.message}`,
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', suburb: '', message: '' });
    } catch (err) {
      setError('Please fill all required fields correctly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-[80vh] pt-[120px]"
      data-testid="hero-section"
      aria-label="Roof painting services hero section"
    >
      {/* Background Image - Optimized with loading lazy */}
      <div className="absolute inset-0">
        <img 
          src="/images/site/hero-bg.jpg"
          alt="Professional roof painting services in Sydney - Beautiful home exterior"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-slate-900/70"></div>
      </div>

      {/* Content */}
      <div className={`relative z-10 ${theme.layout.container} py-8 md:py-12`}>
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Left Content */}
          <div className="text-white pt-2 md:pt-4">
            {/* Main Headline - SEO optimized H1 */}
            <h1 
              className="font-oswald font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight mb-3 leading-none"
              data-testid="hero-headline"
            >
              ROOF PAINTING
            </h1>

            {/* 35% OFF Line */}
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="font-oswald font-black text-3xl sm:text-4xl md:text-5xl text-[#ff5218] uppercase">
                35% OFF
              </span>
              <span className="font-oswald font-bold text-2xl sm:text-3xl md:text-4xl text-white uppercase">
                THIS MONTH
              </span>
            </div>

            {/* Special Offer Badge */}
            <div className="mb-4">
              <span className="inline-block bg-[#ff5218] text-white font-oswald font-bold text-base sm:text-lg px-4 py-2 uppercase italic tracking-wide">
                Special Offer
              </span>
            </div>

            {/* Tagline */}
            <p className="font-playfair italic text-lg sm:text-xl text-white/90 mb-4">
              A Time For Renewal. Make Your Roof New Again.
            </p>

            {/* Stats Line */}
            <p className="font-oswald font-bold text-base sm:text-lg text-white uppercase tracking-wide mb-6">
              10+ YEARS IN BUSINESS - 1000+ HAPPY CUSTOMERS
            </p>

            {/* Trust Badges with Icons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#21c2f8]" aria-hidden="true" />
                <span className="text-white text-sm font-medium">12 Years Warranty</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" aria-hidden="true" />
                <span className="text-white text-sm font-medium">100% Satisfaction</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded flex items-center gap-2">
                <Search className="w-4 h-4 text-[#21c2f8]" aria-hidden="true" />
                <span className="text-white text-sm font-medium">Free Inspection</span>
              </div>
            </div>

            {/* Google Rating Badge */}
            <div className="bg-white rounded-xl shadow-lg p-4 inline-block max-w-[200px]">
              <img 
                src="/images/site/google-rating.png"
                alt="Google 5.0 Star Rating - 167+ Reviews for Sydney Roof Painters"
                className="w-full h-auto max-h-[120px] object-contain"
                loading="eager"
              />
            </div>
          </div>

          {/* Quote Form - Enhanced with icons */}
          <div 
            id="quote-form"
            className="bg-slate-900/80 backdrop-blur-md p-5 md:p-6 rounded-lg shadow-2xl border border-white/10"
          >
            {isSubmitted ? (
              <div className="text-center py-6">
                <CheckCircle className="w-14 h-14 mx-auto mb-3 text-green-500" aria-hidden="true" />
                <h3 className="font-oswald text-white text-xl uppercase mb-2">
                  Thank You!
                </h3>
                <p className="text-slate-300 text-sm">
                  We'll contact you shortly with your free quote.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="mt-4 bg-[#ff5218] hover:bg-[#e54a15] text-white font-oswald font-bold uppercase px-5 py-2 transition-colors"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="quote-form" aria-label="Request a free roof painting quote">
                {/* Form Header */}
                <div className="text-center mb-4">
                  <h2 className="font-oswald font-black text-xl md:text-2xl uppercase">
                    <span className="text-white">Get Your Free</span>
                    <br />
                    <span className="text-[#ff5218]">Roof Painting Quote</span>
                  </h2>
                  <p className="text-slate-300 text-xs mt-1">Fill in your details below and we'll call you back</p>
                </div>

                {/* Form Fields with Icons */}
                <div className="relative">
                  <label className="block text-white text-xs font-medium mb-1" htmlFor="name">
                    Your Name <span className="text-[#ff5218]">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="w-full pl-10 pr-4 py-2.5 bg-white border-0 text-slate-900 text-sm focus:ring-2 focus:ring-[#ff5218] outline-none rounded"
                      data-testid="input-name"
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-white text-xs font-medium mb-1" htmlFor="email">
                    Your Email <span className="text-[#ff5218]">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-white border-0 text-slate-900 text-sm focus:ring-2 focus:ring-[#ff5218] outline-none rounded"
                      data-testid="input-email"
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-white text-xs font-medium mb-1" htmlFor="phone">
                    Phone Number <span className="text-[#ff5218]">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="0412 345 678"
                      className="w-full pl-10 pr-4 py-2.5 bg-white border-0 text-slate-900 text-sm focus:ring-2 focus:ring-[#ff5218] outline-none rounded"
                      data-testid="input-phone"
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-white text-xs font-medium mb-1" htmlFor="suburb">
                    Suburb
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                    <input
                      type="text"
                      id="suburb"
                      name="suburb"
                      value={formData.suburb}
                      onChange={handleChange}
                      placeholder="e.g. Parramatta, Sydney"
                      className="w-full pl-10 pr-4 py-2.5 bg-white border-0 text-slate-900 text-sm focus:ring-2 focus:ring-[#ff5218] outline-none rounded"
                      data-testid="input-suburb"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-white text-xs font-medium mb-1" htmlFor="message">
                    Your Message
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" aria-hidden="true" />
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Tell us about your roof painting project..."
                      className="w-full pl-10 pr-4 py-2.5 bg-white border-0 text-slate-900 text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none rounded"
                      data-testid="input-message"
                    />
                  </div>
                </div>

                {error && <p className="text-[#ff5218] text-xs" role="alert">{error}</p>}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#ff5218] hover:bg-[#e54a15] text-white font-oswald font-black text-lg uppercase py-3 flex items-center justify-center gap-2 transition-colors rounded shadow-lg hover:shadow-xl"
                  data-testid="quote-submit"
                  aria-label="Submit form to get a free roof painting quote"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" aria-hidden="true" />
                      <span>Get A Free Quote</span>
                    </>
                  )}
                </button>

                {/* Trust indicators */}
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3" aria-hidden="true" /> No spam
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" aria-hidden="true" /> Free quote
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
